import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { BookOpen, Plus, Loader2, Copy, Trash2 } from "lucide-react";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  active: "bg-emerald-100 text-emerald-800 border-emerald-300",
  retired: "bg-amber-100 text-amber-800 border-amber-300",
};

function ModuleForm({ initial, isEdit, onSubmit, onCancel, submitting }) {
  const [draft, setDraft] = useState({
    code: initial?.code || "",
    title: initial?.title || "",
    summary_md: initial?.summary_md || "",
    body_md: initial?.body_md || "",
    hours: initial?.hours ?? "",
    tags: (initial?.tags || []).join(", "),
    status: initial?.status || "draft",
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1">
          <Label className="text-xs text-slate-600">Code *</Label>
          <Input data-testid="module-code-input" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} disabled={isEdit} placeholder="M-LDR-01" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-slate-600">Title *</Label>
          <Input data-testid="module-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Hours</Label>
          <Input type="number" step="0.5" value={draft.hours} onChange={(e) => setDraft({ ...draft, hours: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Status</Label>
          <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
            {["draft", "active", "retired"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Tags (comma)</Label>
          <Input value={draft.tags} onChange={(e) => setDraft({ ...draft, tags: e.target.value })} />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600">Summary</Label>
        <Textarea rows={2} value={draft.summary_md} onChange={(e) => setDraft({ ...draft, summary_md: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs text-slate-600">Body (markdown)</Label>
        <Textarea data-testid="module-body-input" rows={6} value={draft.body_md} onChange={(e) => setDraft({ ...draft, body_md: e.target.value })} />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button
          data-testid="module-submit-btn"
          disabled={submitting}
          onClick={() => {
            if (!draft.code?.trim() || !draft.title?.trim()) return toast.error("Code and title required");
            onSubmit({
              ...draft,
              hours: draft.hours ? parseFloat(draft.hours) : null,
              tags: (draft.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
            });
          }}
        >
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function CurriculumTab({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/curriculum-modules`, { credentials: "include" });
      if (r.ok) setItems(await r.json());
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (body) => {
    setSubmitting(true);
    try {
      const url = editing && editing !== "new"
        ? `${API}/v2/workspaces/${workspaceId}/curriculum-modules/${editing.module_id}`
        : `${API}/v2/workspaces/${workspaceId}/curriculum-modules`;
      const method = editing && editing !== "new" ? "PATCH" : "POST";
      const r = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      toast.success(editing && editing !== "new" ? "Module updated" : "Module created");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const duplicate = async (m) => {
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/curriculum-modules/${m.module_id}/duplicate`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error("Duplicate failed");
      const d = await r.json();
      toast.success(`Cloned as v${d.version}`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (m) => {
    if (!confirm(`Delete ${m.code} v${m.version}?`)) return;
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/curriculum-modules/${m.module_id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Delete failed");
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Group by code
  const grouped = items.reduce((acc, m) => { (acc[m.code] = acc[m.code] || []).push(m); return acc; }, {});

  return (
    <div className="space-y-4" data-testid="curriculum-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Versioned methodology library. Modules referenced by programmes and proposals.</p>
        <Button onClick={() => setEditing("new")} data-testid="curriculum-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New module
        </Button>
      </div>

      {loading ? (
        <Card className="border-slate-200"><CardContent className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</CardContent></Card>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="curriculum-empty"><BookOpen className="h-8 w-8 mx-auto text-slate-300 mb-2" />No modules yet.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([code, versions]) => (
            <Card key={code} className="border-slate-200" data-testid={`curriculum-group-${code}`}>
              <CardContent className="p-3">
                <div className="flex items-baseline gap-2">
                  <Badge variant="outline" className="text-xs">{code}</Badge>
                  <span className="text-xs text-slate-500">{versions.length} version{versions.length > 1 ? "s" : ""}</span>
                </div>
                <ul className="mt-2 divide-y divide-slate-100">
                  {versions.map((m) => (
                    <li key={m.module_id} className="py-2" data-testid={`module-row-${m.module_id}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">v{m.version}</Badge>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[m.status] || ""}`}>{m.status}</Badge>
                        <span className="font-medium text-sm text-slate-800 cursor-pointer" onClick={() => setExpanded({ ...expanded, [m.module_id]: !expanded[m.module_id] })}>
                          {m.title}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">{m.hours != null ? `${m.hours}h` : ""}</span>
                        <div className="ml-auto flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditing(m)} data-testid={`module-edit-${m.module_id}`}>Edit</Button>
                          <Button size="sm" variant="ghost" onClick={() => duplicate(m)} data-testid={`module-duplicate-${m.module_id}`} title="Clone as next version"><Copy className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(m)} className="text-red-600" data-testid={`module-delete-${m.module_id}`}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                      {m.summary_md && <p className="text-xs text-slate-500 mt-1">{m.summary_md}</p>}
                      {(m.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {m.tags.map((t, i) => <span key={i} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{t}</span>)}
                        </div>
                      )}
                      {expanded[m.module_id] && m.body_md && (
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-2 mt-2">{m.body_md}</pre>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="module-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-blue-600" />{editing === "new" ? "New module" : `Edit ${editing?.code} v${editing?.version}`}</DialogTitle>
            <DialogDescription>Code is immutable. To create a new version, use the duplicate icon.</DialogDescription>
          </DialogHeader>
          {editing && <ModuleForm initial={editing === "new" ? null : editing} isEdit={editing !== "new"} submitting={submitting} onCancel={() => setEditing(null)} onSubmit={submit} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
